import jsPDF from 'jspdf';

export interface ReceiptInfo {
    paymentCardLast4?: string;
    paymentCardBrand?: string;
    organizer?: { orgName?: string; avatar?: string } | null;
}

export interface ReceiptBuyer {
    name: string;
    email: string;
    phone?: string;
}

const BRAND_NAMES: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
};

const money = (n: number | undefined | null) => `$${(Number(n) || 0).toFixed(2)}`;

async function loadImage(url: string): Promise<{ data: string; w: number; h: number } | null> {
    try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const blob = await res.blob();
        const data = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
        const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
            img.onerror = reject;
            img.src = data;
        });
        return { data, ...dims };
    } catch {
        return null;
    }
}

function fitImage(img: { w: number; h: number }, maxW: number, maxH: number) {
    const ratio = Math.min(maxW / img.w, maxH / img.h);
    return { w: img.w * ratio, h: img.h * ratio };
}

function formatDate(value?: string): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export async function generateOrderReceiptPDF(order: any, buyer: ReceiptBuyer, info: ReceiptInfo): Promise<Blob> {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const left = 16;
    const right = pageW - 16;
    const contentW = right - left;
    const teal = '#008C95';
    const tealLight = '#E6F4F5';
    const dark = '#1F2937';
    const muted = '#6B7280';
    const line = '#E5E7EB';
    const panel = '#F9FAFB';

    const event = order.tickets?.find((t: any) => t.event)?.event;
    const orgName = info.organizer?.orgName || '';

    const [orgLogo, eventupLogo] = await Promise.all([
        info.organizer?.avatar ? loadImage(info.organizer.avatar) : Promise.resolve(null),
        loadImage('/logoColor.png'),
    ]);

    doc.setFillColor(teal);
    doc.rect(0, 0, pageW, 5, 'F');

    let y = 16;
    const headerH = 22;
    let textX = left;
    if (orgLogo) {
        const s = fitImage(orgLogo, 22, headerH);
        doc.addImage(orgLogo.data, 'PNG', left, y + (headerH - s.h) / 2, s.w, s.h);
        textX = left + s.w + 4;
    }
    if (orgName) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(dark);
        const lines = doc.splitTextToSize(orgName, 96 - (textX - left));
        doc.text(lines, textX, y + headerH / 2 - ((lines.length - 1) * 5.5) / 2 + 1.5);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.setTextColor(teal);
    doc.text('RECEIPT', right, y + 9, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(muted);
    doc.text(`Issued ${formatDate(order.createdAt)}`, right, y + 16, { align: 'right' });

    y += headerH + 6;
    doc.setDrawColor(line);
    doc.setLineWidth(0.3);
    doc.line(left, y, right, y);
    y += 8;

    const label = (text: string, x: number, yy: number) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(teal);
        doc.text(text.toUpperCase(), x, yy);
    };

    const block = (x: number, yy: number, width: number, title: string, rows: Array<[string, boolean]>): number => {
        label(title, x, yy);
        let cy = yy + 5.5;
        rows.forEach(([text, bold]) => {
            doc.setFont('helvetica', bold ? 'bold' : 'normal');
            doc.setFontSize(bold ? 10.5 : 9.5);
            doc.setTextColor(bold ? dark : '#4B5563');
            const lines = doc.splitTextToSize(text, width);
            doc.text(lines, x, cy);
            cy += lines.length * 4.6 + 0.8;
        });
        return cy - yy;
    };

    const colW = (contentW - 10) / 2;
    const billedRows: Array<[string, boolean]> = [[buyer.name || '-', true], [buyer.email || '', false]];
    if (buyer.phone) billedRows.push([buyer.phone, false]);
    const eventRows: Array<[string, boolean]> = [[event?.title || '-', true]];
    const venue = [event?.venue, event?.address?.fullAddress].filter(Boolean).join(', ');
    if (venue) eventRows.push([venue, false]);
    const when = formatDate(event?.times?.[0]?.startDate);
    if (when) eventRows.push([when, false]);

    const h1 = block(left, y, colW, 'Billed to', billedRows);
    const h2 = block(left + colW + 10, y, colW, 'Event', eventRows);
    y += Math.max(h1, h2) + 6;

    const boxH = 16;
    doc.setFillColor(panel);
    doc.setDrawColor(line);
    doc.roundedRect(left, y, contentW, boxH, 2, 2, 'FD');
    const metaCell = (title: string, value: string, x: number, width: number) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(muted);
        doc.text(title.toUpperCase(), x, y + 6);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(dark);
        doc.text(doc.splitTextToSize(value || '-', width)[0], x, y + 11.5);
    };
    metaCell('Order number', String(order.orderId || ''), left + 5, 100);
    metaCell('Confirmation code', String(order.hashedUniqueId || ''), left + 112, 30);
    const statusText = String(order.status || '').toLowerCase() === 'completed' ? 'PAID' : String(order.status || '').toUpperCase();
    doc.setFillColor(tealLight);
    doc.roundedRect(right - 26, y + 4, 21, 8, 4, 4, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(teal);
    doc.text(statusText, right - 15.5, y + 9.2, { align: 'center' });
    y += boxH + 10;

    const finalAmount = Number(order.totalAmount) || 0;
    const tickets: any[] = order.tickets || [];
    const ticketDiscount = (t: any) => (Number(t?.promocodeDiscount) || 0) + (Number(t?.memberPricingDiscount) || 0);
    const discount = tickets.reduce((sum, t) => sum + ticketDiscount(t), 0);
    const ticketsNet = tickets.reduce((sum, t) => sum + Math.max(0, (Number(t?.price) || 0) - ticketDiscount(t)), 0);
    const serviceFee = Math.max(0, finalAmount - ticketsNet);

    if (info.paymentCardLast4) {
        const brand = BRAND_NAMES[(info.paymentCardBrand || '').toLowerCase()] || info.paymentCardBrand || 'Card';
        label('Payment method', left, y);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10.5);
        doc.setTextColor(dark);
        doc.text(`${brand} ending in ${info.paymentCardLast4}`, left, y + 6);
        y += 16;
    }

    label('Payment summary', left, y);
    y += 4;
    doc.setDrawColor(line);
    doc.line(left, y, right, y);
    y += 7;

    const row = (name: string, value: string, color = dark, sub = '') => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor('#4B5563');
        doc.text(name, left, y);
        doc.setTextColor(color);
        doc.text(value, right, y, { align: 'right' });
        if (sub) {
            doc.setFontSize(8.5);
            doc.setTextColor(muted);
            doc.text(doc.splitTextToSize(sub, contentW - 30), left, y + 4.5);
            y += 4.5;
        }
        y += 4;
        doc.setDrawColor(line);
        doc.line(left, y, right, y);
        y += 6;
    };

    tickets.forEach((t) => {
        const name = t?.ticketId?.name || t?.ticketTitle || 'Ticket';
        const sd = t?.seatData;
        const seat = sd && Object.keys(sd).length
            ? `Section ${sd.zoneCode || sd.zone || '-'}, Row ${sd.row || '-'}, Seat ${sd.seat || '-'}`
            : '';
        const details = [t?.attendeeName, seat, formatDate(t?.selectedDate)].filter(Boolean).join('  |  ');
        row(name, money(t?.price), dark, details);
    });
    if (discount > 0) row(order.promoCode ? `Discount (${order.promoCode})` : 'Discount', `-${money(discount)}`, '#15803D');
    if (serviceFee >= 0.005) row('Service fee', money(serviceFee));

    y += 2;
    doc.setFillColor(teal);
    doc.roundedRect(left, y, contentW, 14, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor('#FFFFFF');
    doc.text('Total payment', left + 6, y + 9);
    doc.setFontSize(14);
    doc.text(money(finalAmount), right - 6, y + 9.3, { align: 'right' });
    y += 14 + 10;

    const footerY = pageH - 18;
    doc.setDrawColor(line);
    doc.line(left, footerY - 6, right, footerY - 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(muted);
    doc.text('Thank you for your purchase.', left, footerY);
    doc.text(`Generated by Eventup on ${formatDate(new Date().toISOString())}`, left, footerY + 5);
    if (eventupLogo) {
        const s = fitImage(eventupLogo, 30, 9);
        doc.addImage(eventupLogo.data, 'PNG', right - s.w, footerY - 4, s.w, s.h);
    }

    return doc.output('blob');
}
